import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import { ProfileContext } from "../../../context/ProfileProvider";
import AuthService from "../../../api/services/AuthService";
import { SIDE_OPTIONS, buildPairFilterOptions, spotPairsDropdownToApi, spotSideDropdownToApi } from "./spotOrderFilterConstants";
import { mapMeTradeUiRow } from "./spotMeActivityHelpers";
import { spotActivityDefaultLocalRange } from "./orderFilterHelpers";
import { spotMeActivityItemsFromResponse } from "../../../api/services/spotMeActivityQuery";
// Demo merge disabled — API-only spot trade history
// import { mergeSpotProfileDemoRows, SPOT_TRADE_HISTORY_DEMO_ROWS } from "./spotProfileOrdersStatic";
import SpotOrdersExportModal from "./SpotOrdersExportModal";
import OrderHistoryDownloadButtons from "./OrderHistoryDownloadButtons";
import { hideThenShowExport, showExportModal } from "./orderExportHelpers";
import { EXPORT_KIND_SPOT_TRADES } from "./ordersHistoryExport";
import OrderTablePagination, { useOrderTablePagination } from "./OrderTablePagination";

const EXPORT_SPOT_TRADE_HISTORY_MODAL_ID = "exportSpotTradeHistoryModal";
const MOB_TRADE_HISTORY_DL_SEL = "#mob-spot-position-history-filters-dl";

const ORDER_DESKTOP_COL_COUNT = 8;

function DatePickerCell({ value, onChange, ariaLabel, inputRef }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">
        {value}
      </span>
      <input
        ref={inputRef}
        type="date"
        className="so-dateCell__native"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
    </div>
  );
}

const SpotPositionHistory = () => {
  const { pairs: pairsFromProfile, fetchPairs } = useContext(ProfileContext) || {};

  const { from: df0, to: dt0 } = spotActivityDefaultLocalRange();

  const [selectedPair, setSelectedPair] = useState("all");
  const [sideFilter, setSideFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(df0);
  const [dateTo, setDateTo] = useState(dt0);
  const [rows, setRows] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [exportPreset, setExportPreset] = useState("custom");
  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(rows);

  const dateFromRefDesk = useRef(null);
  const dateToRefDesk = useRef(null);
  const dateFromRefMob = useRef(null);
  const dateToRefMob = useRef(null);

  const pairFilterOptions = useMemo(() => buildPairFilterOptions(pairsFromProfile), [pairsFromProfile]);

  const apiPairParam = useMemo(() => spotPairsDropdownToApi(selectedPair), [selectedPair]);

  useEffect(() => {
    if ((!pairsFromProfile || pairsFromProfile.length === 0) && fetchPairs) {
      void fetchPairs();
    }
  }, [pairsFromProfile, fetchPairs]);

  useEffect(() => {
    if (selectedPair === "all") return;
    const ok = pairFilterOptions.some((o) => o.value === selectedPair);
    if (!ok) setSelectedPair("all");
  }, [pairFilterOptions, selectedPair]);

  const loadTrades = useCallback(async () => {
    const res = await AuthService.spotMeTrades({
      page: 1,
      page_size: 100,
      pair: apiPairParam || undefined,
      side: spotSideDropdownToApi(sideFilter) || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    });
    const items = spotMeActivityItemsFromResponse(res);
    const mapped = items.map(mapMeTradeUiRow);
    setRows(mapped);
    // setRows(mergeSpotProfileDemoRows(mapped, SPOT_TRADE_HISTORY_DEMO_ROWS));
  }, [apiPairParam, dateFrom, dateTo, sideFilter]);

  useEffect(() => {
    void loadTrades();
  }, [loadTrades]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const resetFilters = () => {
    const { from, to } = spotActivityDefaultLocalRange();
    setSelectedPair("all");
    setSideFilter("all");
    setDateFrom(from);
    setDateTo(to);
  };

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (el && typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        /* fall through */
      }
    }
    el?.click();
  };

  const openDownload = () => {
    setExportPreset("custom");
    showExportModal(EXPORT_SPOT_TRADE_HISTORY_MODAL_ID);
  };

  const openDownloadFromMobile = () => {
    setExportPreset("custom");
    hideThenShowExport(MOB_TRADE_HISTORY_DL_SEL, EXPORT_SPOT_TRADE_HISTORY_MODAL_ID);
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const fromRef = keyPrefix === "mob" ? dateFromRefMob : dateFromRefDesk;
    const toRef = keyPrefix === "mob" ? dateToRefMob : dateToRefDesk;
    return (
    <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter spot trade history">
      <div className="so-toolbar__left fullwidthdesktop">
        <CustomDropdown
          id={`${pref}sph-dd-pair`}
          label="Pair"
          value={selectedPair}
          options={pairFilterOptions}
          onChange={setSelectedPair}
          menuKey={`${pref}sph-pair`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}sph-dd-side`}
          label="Side"
          value={sideFilter}
          options={SIDE_OPTIONS}
          onChange={setSideFilter}
          menuKey={`${pref}sph-side`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <div className="so-dateBlock">
          <span className="so-dateBlock__label">Date</span>
          <div className="so-dateRow">
            <DatePickerCell value={dateFrom} onChange={setDateFrom} ariaLabel="Start date" inputRef={fromRef} />
            <span className="so-dateArrow" aria-hidden="true">
              →
            </span>
            <DatePickerCell value={dateTo} onChange={setDateTo} ariaLabel="End date" inputRef={toRef} />
            <button type="button" className="so-dateCalBtn" aria-label="Open calendar" onClick={() => openDatePicker(toRef)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.35" />
                <path d="M3 10h18" stroke="currentColor" strokeWidth="1.35" />
                <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div className="so-toolbar__right">
        {!stacked ? (
          <OrderHistoryDownloadButtons onDownload={openDownload} />
        ) : null}
      </div>
    </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={8} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-spot-position-history-title">
            <h1 id="so-spot-position-history-title" className="so-page__title">
              Trade History
            </h1>

            <OrdersSubTabs
              tabs={[
                { label: "Open Orders", to: "/user_profile/orders/spot_orders" },
                { label: "Order History", to: "/user_profile/orders/spot_order_history" },
                { label: "Trade History", to: "/user_profile/orders/spot_position_history" },
              ]}
            />

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap so-tableWrap--staking">
              <table className="so-table so-table--rowLines so-table--wide">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Pair</th>
                    <th scope="col">Side</th>
                    <th scope="col">Price</th>
                    <th scope="col">Filled</th>
                    <th scope="col">Fee</th>
                    {/* <th scope="col">TDS Role</th> */}
                    <th scope="col">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0
                    ? renderEmptyBody()
                    : pagedRows.map((r) => (
                        <tr key={r.id}>
                          <td className="so-num">{r.date}</td>
                          <td className="so-num">{r.pair}</td>
                          <td>
                            <span className={r.sideBuy ? "so-side--buy" : "so-side--sell"}>{r.sideLabel}</span>
                          </td>
                          <td className="so-num">{r.price}</td>
                          <td className="so-num">{r.filled}</td>
                          <td className="so-num">{r.fee}</td>
                          {/* <td className="so-num">{r.tdsRole}</td> */}
                          <td className="so-num">{r.total}</td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>

            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="Trade history pages"
            />
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Spot Trade History</h2>
          <OrdersSubTabs
            tabs={[
              { label: "Open Orders", to: "/user_profile/orders/spot_orders" },
              { label: "Order History", to: "/user_profile/orders/spot_order_history" },
              { label: "Trade History", to: "/user_profile/orders/spot_position_history" },
            ]}
          />
          <div className="so-mobile__toolbar">
            <SoMobileFilters
              modalId="mob-spot-position-history-filters"
              toolbar={renderToolbar(true, "mob")}
              onReset={resetFilters}
              downloads={
                <OrderHistoryDownloadButtons stacked onDownload={openDownloadFromMobile} />
              }
            />
          </div>
          {rows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            pagedRows.map((r) => (
              <OrderMobileCardShell key={`m-${r.id}`} desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Spot trade history details">
                <div className="so-mobileCard__row">
                  <span>Date</span>
                  <span className="so-num">{r.date}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Pair</span>
                  <span className="so-num">{r.pair}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Side</span>
                  <span className={r.sideBuy ? "so-side--buy" : "so-side--sell"}>{r.sideLabel}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Price</span>
                  <span className="so-num">{r.price}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Filled</span>
                  <span className="so-num">{r.filled}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Fee</span>
                  <span className="so-num">{r.fee}</span>
                </div>
                {/* <div className="so-mobileCard__row">
                  <span>TDS Role</span>
                  <span className="so-num">{r.tdsRole}</span>
                </div> */}
                <div className="so-mobileCard__row">
                  <span>Total</span>
                  <span className="so-num">{r.total}</span>
                </div>
              </OrderMobileCardShell>
            ))
          )}

          <OrderTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label="Trade history pages"
          />
        </div>
      </div>
      <SpotOrdersExportModal
        modalId={EXPORT_SPOT_TRADE_HISTORY_MODAL_ID}
        exportKind={EXPORT_KIND_SPOT_TRADES}
        title="AGCX Trade History"
        defaultFrom={dateFrom}
        defaultTo={dateTo}
        defaultPreset={exportPreset}
      />
    </div>
  );
};

export default SpotPositionHistory;
